import { useEffect, useState } from "react";

import { isApiError } from "../../../services/api/errors";
import {
  DnsRecord,
  DOMAIN_SETTINGS_ERRORS,
  DomainDns,
  getDomainDns,
  saveDomainDns,
  setDomainNameservers,
} from "../../../services/domains";

const EMPTY_RECORD: DnsRecord = { id: "", type: "A", host: "", value: "", ttl: "auto" };

// A host name for a nameserver: labels of letters, digits and hyphens, at least two of them
const NAMESERVER = /^(?!-)[a-z0-9-]{1,63}(\.[a-z0-9-]{1,63})+$/i;

/**
 * A domain's DNS on its page: the records and the nameservers (Keepo's or the person's own), what is being
 * edited or removed, and saving them. `setSaving` is the page's: other changes (the Keepo) share the "saving".
 */
export const useDomainDns = ({
  domain,
  isPending,
  setSaving,
}: {
  domain: string;
  isPending: boolean;
  setSaving: (isSaving: boolean) => void;
}) => {
  const [dns, setDns] = useState<DomainDns>();
  const [dnsError, setDnsError] = useState(false);
  const [nsMode, setNsMode] = useState<"keepo" | "custom">("keepo");
  const [nameservers, setNameservers] = useState(["", ""]);
  const [nameserversError, setNameserversError] = useState<"invalid" | "provider">();
  const [isSwitchOpen, setSwitchOpen] = useState(false);
  // The record being added ("" id) or edited, and why it can't be saved
  const [draft, setDraft] = useState<DnsRecord>();
  const [draftError, setDraftError] = useState<string>();
  const [removing, setRemoving] = useState<DnsRecord>();

  const showDns = (next: DomainDns) => {
    setDns(next);
    setNsMode(next.mode);
    if (next.mode === "custom") {
      setNameservers(next.nameservers.length >= 2 ? next.nameservers : [...next.nameservers, "", ""].slice(0, 2));
    }
  };

  useEffect(() => {
    if (isPending) {
      return;
    }
    getDomainDns(domain)
      .then(showDns)
      .catch(() => setDnsError(true));
  }, [domain, isPending]);

  const records = dns?.records ?? [];
  const managed = records.filter(record => record.managed);
  const ownRecords = records.filter(record => !record.managed);

  // Cancelled: the form goes, and so does the reason it couldn't be saved
  const cancelDraft = () => {
    setDraft(undefined);
    setDraftError(undefined);
  };

  const startDraft = (record: DnsRecord = EMPTY_RECORD) => {
    setDraft({ ...record });
    setDraftError(undefined);
  };

  // The API answers why a record can't be saved: a refused setting says which (Keepo's names, a CNAME on the
  // domain itself, or just invalid); anything else (the registrar out of reach, a network failure) is the provider
  const errorOf = (error: unknown) => {
    if (!isApiError(error, DOMAIN_SETTINGS_ERRORS.invalid)) {
      return "provider";
    }
    const message = String((error as { response?: { data?: { message?: string } } })?.response?.data?.message || "");
    return message.includes("MANAGED_BY_KEEPO") ? "managed" : message.includes("CNAME_ROOT") ? "cnameRoot" : "invalid";
  };

  const saveRecords = async (next: DnsRecord[]) => {
    setSaving(true);
    setDraftError(undefined);
    try {
      showDns(await saveDomainDns(domain, next));
      return true;
    } catch (error) {
      setDraftError(errorOf(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) {
      return;
    }
    if (!draft.value.trim()) {
      setDraftError("value");
      return;
    }
    const next = draft.id
      ? ownRecords.map(item => (item.id === draft.id ? draft : item))
      : [...ownRecords, { ...draft, id: "new" }];
    if (await saveRecords(next)) {
      setDraft(undefined);
    }
  };

  const saveNameservers = async (list: string[]) => {
    if (list.some(item => !NAMESERVER.test(item.trim()))) {
      setNameserversError("invalid");
      return;
    }
    setSaving(true);
    setNameserversError(undefined);
    try {
      showDns(await setDomainNameservers(domain, list));
    } catch (error) {
      // Refused (fewer than two, or not host names) is not the provider being down
      setNameserversError(isApiError(error, DOMAIN_SETTINGS_ERRORS.invalid) ? "invalid" : "provider");
    } finally {
      setSaving(false);
    }
  };

  return {
    dns,
    dnsError,
    showDns,
    records,
    managed,
    ownRecords,
    nsMode,
    setNsMode,
    nameservers,
    setNameservers,
    nameserversError,
    isSwitchOpen,
    setSwitchOpen,
    draft,
    setDraft,
    draftError,
    removing,
    setRemoving,
    startDraft,
    cancelDraft,
    saveRecords,
    saveDraft,
    saveNameservers,
  };
};
